import { useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import {
  GridColumnsHookParamsModel,
  TableActionColumnsStyleEnum,
  ActionTypeEnum
} from "../../models";
import { GridColumnsResponseModel } from "../../models";
import { generateGridColumns } from "../../components/common/dataTable/grid/utils/generateGridColumns";
import {
  generateGridColumnsWithActions
} from "../../components/common/dataTable/grid/utils/generateGridColumnsWithActions";
import {getFormPrivileges} from "../../utils";
import {getStaticColumns} from "../../components/common/dataTable/grid/rendering/renderGridStaticColumns";

// Extend the original interface to include the row action resolver
interface useGridColumnsWithActionsProps<T> extends GridColumnsHookParamsModel<T> {
  data: any[];
  getRowActions?: (row: any,rowIndex:number) => ActionTypeEnum[]; // Add function to resolve row-specific actions
}

/**
 * Enhanced version of useGridColumns that supports row-based action resolution
 */
const useGridColumnsWithActions = <T>({
                                        isArabic,
                                        onActionEvent,
                                        actions,
                                        actionsColumnGrow,
                                        data,
                                        getRowActions, // Resolver for row-specific actions
                                      }: useGridColumnsWithActionsProps<T>, style: TableActionColumnsStyleEnum | null, formId?: number | null) => {
  const [gridColumns, setGridColumns] = useState<TableColumn<T>[]>([]);
  const [columnsSettings, setColumnsSettings] = useState<GridColumnsResponseModel[]>([]);
  const [isGridColumnsLoading, setIsGridColumnsLoading] = useState(false);

  const fetchGridColumns = async () => {
    if (data.length === 0) return;
    setIsGridColumnsLoading(true);

    try {
      // IMPORTANT CHANGE: Don't use generateGridColumns which adds its own actions column
      // Instead, get only the static data columns without actions
      const gridId = getFormPrivileges(formId || 0).ColumnsSettingId || 0;
      const { gridColumns: dataColumns, settingsColumns } = await getStaticColumns({
        gridId,
        data,
        isArabic,
      });

      // Then use generateGridColumnsWithActions to add just ONE actions column
      const { gridCols: actionColumns } = await generateGridColumnsWithActions<T>({
        actions,
        formId,
        isArabic,
        onActionEvent,
        actionsColumnGrow,
        data,
        style,
        getRowActions,
      });

      // Combine data columns with the single actions column
      const finalColumns = [...dataColumns, ...actionColumns];

      setIsGridColumnsLoading(false);
      setColumnsSettings(settingsColumns.map((column) => ({
        ...column,
        Width: column.Width === null ? 10 : column.Width
      })));
      setGridColumns(finalColumns);
    } catch (error) {
      console.error("Error generating grid columns:", error);
      setIsGridColumnsLoading(false);
    }
  };

  useEffect(() => {
    fetchGridColumns().then();
  }, [data]);

  return {
    gridColumns,
    fetchGridColumns,
    isGridColumnsLoading,
    columnsSettings,
    onActionEvent,
  };
};

export default useGridColumnsWithActions;